// src/pages/MeetingList.jsx
import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";

const MeetingList = () => {
  const [loadingUser, setLoadingUser] = useState(true);
  const [systemRole, setSystemRole] = useState("");
  const [username, setUsername] = useState("");
  const [companyName, setCompanyName] = useState("");

  const [loading, setLoading] = useState(false);
  const [invites, setInvites] = useState([]);
  const [error, setError] = useState("");

  // Load user
  useEffect(() => {
    const fetchMe = async () => {
      try {
        const token = localStorage.getItem("accessToken");
        if (!token) {
          setError("You must be logged in.");
          setLoadingUser(false);
          return;
        }
        const res = await axios.get("/api/users/me/", {
          headers: { Authorization: `Bearer ${token}` },
        });

        const uname = res.data.username || "";
        const srole = res.data.system_role || "";
        const company = res.data.company?.company_name || "";

        setUsername(uname);
        setSystemRole(srole);
        setCompanyName(company);

        localStorage.setItem("username", uname);
        localStorage.setItem("system_role", srole);

      } catch (err) {
        console.error("Error loading user:", err);
        setError("Unable to load user details.");
      } finally {
        setLoadingUser(false);
      }
    };
    fetchMe();
  }, []);

  // Load meetings for this Head's company
  const fetchMeetings = async () => {
    try {
      setLoading(true);
      setError("");

      const token = localStorage.getItem("accessToken");

      const res = await axios.get("/api/meetings/list/", {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });

      // IMPORTANT FIX:
      setInvites(res.data.items || []);

    } catch (err) {
      console.error("Error loading meetings:", err);
      setError("Failed to load meeting list.");
      setInvites([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!loadingUser && systemRole === "Head") {
      fetchMeetings();
    }
  }, [loadingUser, systemRole]);

  // Group by meeting_id
  const groupedMeetings = useMemo(() => {
    const groups = {};

    invites.forEach((row) => {
      const id = row.meeting_id || "UNKNOWN";

      if (!groups[id]) {
        groups[id] = {
          header: {
            meeting_id: row.meeting_id,
            title: row.meeting_title,
            scheduled_datetime: `${row.meeting_date} ${row.meeting_time}`,
            platform: row.meeting_platform,
            meeting_link: row.meeting_link,
            agenda: row.description,
            created_at: row.created_at,
          },
          invitees: [],
        };
      }

      groups[id].invitees.push({
        invitee_username: row.invitee_username,
        invitee_email: row.invitee_email,
        email_sent: row.email_sent,
      });
    });

    return Object.values(groups).sort((a, b) => {
      const da = new Date(a.header.scheduled_datetime || a.header.created_at || 0);
      const db = new Date(b.header.scheduled_datetime || b.header.created_at || 0);
      return db - da;
    });
  }, [invites]);

  const formatDateTime = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString();
  };

  if (loadingUser) {
    return (
      <div style={styles.page}>
        <div style={styles.loadingBox}>Loading...</div>
      </div>
    );
  }

  if (systemRole !== "Head") {
    return (
      <div style={styles.page}>
        <div style={styles.deniedBox}>
          <h2>Access Restricted</h2>
          <p>Only Head users can view previous meetings.</p>
          <button
            style={styles.backBtn}
            onClick={() => (window.location.href = "/dashboard")}
          >
            ⬅ Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      {/* Header */}
      <div style={styles.headerRow}>
        <h1 style={styles.title}>Previous ISO Mandated Meetings</h1>
        <button
          style={styles.backBtn}
          onClick={() => (window.location.href = "/policy-review")}
        >
          ⬅ Back to ISO Mandated Meetings
        </button>
      </div>

      {/* User Info */}
      <div style={styles.infoRow}>
        <span><strong>User:</strong>{" "}</span>
        <span style={styles.chip}>{username}</span>
        <span style={{ marginLeft: 10 }}><strong>Company:</strong>{" "}</span>
        <span style={styles.chip}>{companyName || "N/A"}</span>
      </div>

      {error && <div style={styles.errorMsg}>{error}</div>}

      <div style={styles.card}>
        {loading ? (
          <div>Loading meeting list...</div>
        ) : groupedMeetings.length === 0 ? (
          <div>No meetings found for this company.</div>
        ) : (
          groupedMeetings.map((meeting, idx) => (
            <div key={idx} style={styles.meetingBlock}>
              {/* Meeting Header */}
              <div style={styles.meetingHeader}>
                <div>
                  <div style={styles.meetingTitle}>
                    {meeting.header.title || "Untitled Meeting"}
                  </div>

                  <div style={styles.meetingMeta}>
                    <span><strong>ID:</strong> {meeting.header.meeting_id || "—"}</span>

                    <span style={{ marginLeft: 12 }}>
                      <strong>When:</strong>{" "}
                      {formatDateTime(meeting.header.scheduled_datetime)}
                    </span>

                    <span style={{ marginLeft: 12 }}>
                      <strong>Platform:</strong>{" "}
                      {meeting.header.platform || "N/A"}
                    </span>
                  </div>

                  {meeting.header.meeting_link && (
                    <div style={styles.meetingLinkRow}>
                      <strong>Link:</strong>{" "}
                      <a href={meeting.header.meeting_link} target="_blank" rel="noreferrer">
                        {meeting.header.meeting_link}
                      </a>
                    </div>
                  )}

                  {meeting.header.agenda && (
                    <div style={styles.meetingAgenda}>
                      <strong>Agenda:</strong> {meeting.header.agenda}
                    </div>
                  )}
                </div>
              </div>

              {/* Invitees Table */}
              <div style={styles.tableWrapper}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th>Invitee Username</th>
                      <th>Email</th>
                      <th>Email Sent</th>
                    </tr>
                  </thead>
                  <tbody>
                    {meeting.invitees.map((inv, i2) => (
                      <tr key={i2}>
                        <td>{inv.invitee_username}</td>
                        <td>{inv.invitee_email}</td>
                        <td>{inv.email_sent ? "Yes" : "No"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {idx < groupedMeetings.length - 1 && (
                <hr style={styles.divider} />
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

const styles = {
  page: {
    minHeight: "100vh",
    padding: "30px",
    background: "linear-gradient(135deg, #f6f8fc, #e4ebff)",
    fontFamily: "Segoe UI, Roboto, sans-serif",
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  title: {
    fontSize: 26,
    fontWeight: 700,
    color: "#111827",
    margin: 0,
  },
  backBtn: {
    background: "#6b7280",
    color: "white",
    border: "none",
    padding: "8px 14px",
    borderRadius: 8,
    cursor: "pointer",
  },
  infoRow: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    marginBottom: 16,
  },
  chip: {
    background: "#eef2ff",
    padding: "4px 10px",
    borderRadius: 999,
  },
  card: {
    background: "#fff",
    borderRadius: 14,
    padding: 20,
    boxShadow: "0 8px 20px rgba(15,23,42,0.08)",
  },
  meetingBlock: {
    marginBottom: 18,
  },
  meetingHeader: {
    marginBottom: 10,
  },
  meetingTitle: {
    fontSize: 18,
    fontWeight: 600,
    marginBottom: 4,
  },
  meetingMeta: {
    fontSize: 14,
    color: "#4b5563",
  },
  meetingLinkRow: {
    marginTop: 4,
    fontSize: 14,
  },
  meetingAgenda: {
    marginTop: 4,
    fontSize: 14,
    color: "#374151",
  },
  tableWrapper: {
    overflowX: "auto",
    marginTop: 8,
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 14,
  },
  divider: {
    marginTop: 16,
    borderColor: "#e5e7eb",
  },
  loadingBox: {
    marginTop: 100,
    textAlign: "center",
    fontSize: 18,
  },
  deniedBox: {
    margin: "80px auto",
    maxWidth: 480,
    background: "#fff",
    padding: 24,
    borderRadius: 14,
    textAlign: "center",
    boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
  },
  errorMsg: {
    marginBottom: 10,
    padding: 8,
    borderRadius: 8,
    background: "#fee2e2",
    color: "#991b1b",
  },
};

export default MeetingList;
